import { batch, createMemo, createSignal, type Accessor } from 'solid-js';

import type { SelectionTreeExpansion } from './selection-tree-expansion.types';

const withAddedIds = (
  current: ReadonlySet<string>,
  ids: readonly string[]
): ReadonlySet<string> => {
  let changed = false;
  const next = new Set(current);
  for (const id of ids) {
    if (next.has(id)) continue;
    next.add(id);
    changed = true;
  }
  return changed ? next : current;
};

const withRemovedIds = (
  current: ReadonlySet<string>,
  ids: readonly string[]
): ReadonlySet<string> => {
  let changed = false;
  const next = new Set(current);
  for (const id of ids) {
    if (!next.delete(id)) continue;
    changed = true;
  }
  return changed ? next : current;
};

export const createSelectionTreeExpansion = (
  collapseContextKey: Accessor<string>
): SelectionTreeExpansion => {
  const [defaultExpandedIds, setDefaultExpandedIds] = createSignal<
    ReadonlySet<string>
  >(new Set());
  const [userOpenIds, setUserOpenIds] = createSignal<ReadonlySet<string>>(
    new Set()
  );
  const [persistentClosedIds, setPersistentClosedIds] = createSignal<
    ReadonlySet<string>
  >(new Set());
  const [everUserOpenIds, setEverUserOpenIds] = createSignal<
    ReadonlySet<string>
  >(new Set());
  const [userCollapsedState, setUserCollapsedState] = createSignal<{
    readonly contextKey: string;
    readonly nodeIds: ReadonlySet<string>;
  }>({
    contextKey: '',
    nodeIds: new Set<string>(),
  });

  const contextCollapsedIds = createMemo(() => {
    const state = userCollapsedState();
    return state.contextKey === collapseContextKey()
      ? state.nodeIds
      : new Set<string>();
  });

  const everExpandedIds = createMemo(() => {
    const ids = new Set(defaultExpandedIds());
    for (const id of everUserOpenIds()) {
      ids.add(id);
    }
    return ids;
  });

  const setContextCollapsed = (nodeId: string, collapsed: boolean): void => {
    const contextKey = collapseContextKey();
    setUserCollapsedState((prev) => {
      const nodeIds =
        prev.contextKey === contextKey
          ? new Set(prev.nodeIds)
          : new Set<string>();
      if (collapsed) {
        nodeIds.add(nodeId);
      } else {
        nodeIds.delete(nodeId);
      }
      return { contextKey, nodeIds };
    });
  };

  const addDefaultExpandIds = (ids: readonly string[]): void => {
    if (ids.length === 0) return;
    // One transition. New default ids reopen a previous user close.
    // The current filter context can still keep that row collapsed.
    batch(() => {
      setDefaultExpandedIds((current) => withAddedIds(current, ids));
      setPersistentClosedIds((current) => withRemovedIds(current, ids));
    });
  };

  const toggleExpanded = (nodeId: string, nextExpanded: boolean): void => {
    batch(() => {
      if (nextExpanded) {
        setUserOpenIds((current) => withAddedIds(current, [nodeId]));
        setEverUserOpenIds((current) => withAddedIds(current, [nodeId]));
        setPersistentClosedIds((current) => withRemovedIds(current, [nodeId]));
        setContextCollapsed(nodeId, false);
        return;
      }

      setUserOpenIds((current) => withRemovedIds(current, [nodeId]));
      setPersistentClosedIds((current) => withAddedIds(current, [nodeId]));
      setContextCollapsed(nodeId, true);
    });
  };

  const isExpanded = (
    nodeId: string,
    filterExpandIds: ReadonlySet<string>
  ): boolean => {
    if (contextCollapsedIds().has(nodeId)) {
      return false;
    }
    if (filterExpandIds.has(nodeId)) {
      return true;
    }
    if (persistentClosedIds().has(nodeId)) {
      return false;
    }
    return defaultExpandedIds().has(nodeId) || userOpenIds().has(nodeId);
  };

  return {
    isExpanded,
    everExpandedIds,
    addDefaultExpandIds,
    toggleExpanded,
  };
};
